import { useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectTemplateResource, Repository, ShareProjectTemplateToManySpacesCommand } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useEffect, useState } from "react";
import TemplateWizardDialogLayout from "~/areas/platformHub/TemplateWizardDialogLayout";
import { ShareTemplateDialog, ShareTemplateSpacesType } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import CustomDialog from "~/components/Dialog/CustomDialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useProjectTemplateWizardDialog } from "./ProjectTemplateWizardDialogContext";
export default function ProjectTemplateOverflowMenu({ projectTemplate }: {
    projectTemplate: ProjectTemplateResource;
}) {
    const { result: projectTemplateLink } = useQuery((repo: Repository) => repo.ProjectTemplates.getLink(projectTemplate.Slug, projectTemplate.GitRef), [projectTemplate.Slug, projectTemplate.GitRef], "Get project template link");
    const { isOpen: isShareDialogOpen, openDialog: openShareDialog, closeAllDialogs: closeShareDialog } = useDialogTrigger();
    const { isInProgress, errors } = useAggregateAPIOperationStatus();
    const [canSaveShare, setCanSaveShare] = useState<boolean>(false);
    const [shareType, setShareType] = useState<ShareTemplateSpacesType>(ShareTemplateSpacesType.NotShared);
    const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
    const { sharingConfiguration, reloadSharingConfiguration, refetchProjectTemplateStatus } = useProjectTemplateWizardDialog();
    useEffect(() => {
        if (isShareDialogOpen && sharingConfiguration) {
            if (sharingConfiguration.SharedToAllSpaces) {
                setShareType(ShareTemplateSpacesType.ShareAll);
                setSelectedSpaces([]);
            }
            else if (sharingConfiguration.IndividuallySharedSpaceIds.length > 0) {
                setShareType(ShareTemplateSpacesType.ShareIndividual);
                setSelectedSpaces(sharingConfiguration.IndividuallySharedSpaceIds);
            }
            else {
                setShareType(ShareTemplateSpacesType.NotShared);
                setSelectedSpaces([]);
            }
            setCanSaveShare(false);
        }
    }, [isShareDialogOpen, sharingConfiguration]);
    const { result: spaces } = useQuery((repo: Repository) => repo.Spaces.all(), [], "Get spaces for project template share");
    const { execute: shareProjectTemplate } = useMutation({
        name: "Share project template",
        action: async (repo: Repository) => {
            const cmd: ShareProjectTemplateToManySpacesCommand = {
                ProjectTemplateSlug: projectTemplate.Slug,
                GitRef: projectTemplate.GitRef,
                ShareToAllSpaces: shareType === ShareTemplateSpacesType.ShareAll,
                IndividuallySharedSpaceIds: shareType === ShareTemplateSpacesType.ShareIndividual ? selectedSpaces : [],
            };
            await repo.ProjectTemplates.shareToSpace(cmd);
        },
        afterComplete: async () => {
            reloadSharingConfiguration();
            refetchProjectTemplateStatus();
            closeShareDialog();
        },
    });
    const overflowActions: Array<MenuItem | MenuItem[] | undefined | null> = [];
    if (isAllowed({ permission: Permission.PlatformHubEdit })) {
        overflowActions.push(OverflowMenuItems.item("Share", openShareDialog));
    }
    if (projectTemplateLink?.Link) {
        overflowActions.push(OverflowMenuItems.item("View source code", () => window.open(projectTemplateLink.Link, "_blank", "noopener,noreferrer")));
    }
    else {
        overflowActions.push(OverflowMenuItems.disabledItem("View source code", "Link unavailable"));
    }
    return (<>
            <OverflowMenu menuItems={overflowActions}/>

            <CustomDialog open={isShareDialogOpen} close={closeShareDialog} render={() => (<TemplateWizardDialogLayout onSaveClick={async () => {
                await shareProjectTemplate();
                return true;
            }} busy={isInProgress} errors={errors} savePermission={{ permission: Permission.PlatformHubEdit }} pages={[
                {
                    title: "Share to...",
                    content: <ShareTemplateDialog spaces={spaces ?? []} shareType={shareType} selectedSpaces={selectedSpaces} setShareType={setShareType} setSelectedSpaces={setSelectedSpaces} setCanSave={setCanSaveShare}/>,
                    actionAvailable: canSaveShare,
                    statusBarLabel: "Share",
                    singlePageSaveLabel: "Share",
                    singlePageSavingLabel: "Sharing...",
                },
            ]}/>)}/>
        </>);
}
